import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { calendlyUrl, type CalendlyLinkOptions, type CalendlyMedium } from '../lib/calendly'

const PINK = '#FB3AAE'

/**
 * ManagedServicesBanner — the primary managed-services call surface (dev call
 * 2026-09-04: the forced onboarding call died; the call is now a qualified
 * managed-services pitch, and it lives in the dashboard).
 *
 * Deliberately intent-first: a button, not an embedded calendar — clicking
 * through IS the qualification, so sales only talks to people who already
 * raised their hand. Dismissible; ✕ persists per browser.
 *
 * Parameterised because the agency lobby needs the same pitch aimed at a
 * roster rather than a single brand, with its own dismissal and its own
 * utm_medium. Defaults are the brand-Home copy, so a call site that wants that
 * still passes nothing but the link target.
 *
 * The href goes through calendlyUrl rather than being hand-written: utm_content
 * is what lets the Calendly webhook stamp the booking against the right brand
 * or account, and a hard-coded link silently drops it (see src/lib/calendly.ts).
 */
export default function ManagedServicesBanner({
  medium = 'managed_home',
  dismissKey = 'bmc_managed_home_banner_dismissed',
  title = 'Want us to run this for you?',
  body = 'Our team manages campaigns end to end — creator picks, rate negotiation, and content review. Book a quick call and see if managed is a fit.',
  link = {},
}: {
  medium?: CalendlyMedium
  /** localStorage key for the ✕. Distinct per placement so dismissing one doesn't hide the other. */
  dismissKey?: string
  title?: string
  body?: string
  /** Who is booking — brandId or accountId, so the webhook can resolve it. */
  link?: CalendlyLinkOptions
}) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === '1' } catch { return false }
  })
  if (dismissed) return null

  const dismiss = () => {
    setDismissed(true)
    try { localStorage.setItem(dismissKey, '1') } catch { /* session-only */ }
  }

  return (
    <div style={{
      background: 'rgba(251, 58, 174, 0.07)',
      border: '1px solid rgba(251, 58, 174, 0.3)',
      borderRadius: 14,
      padding: '16px 20px',
      marginBottom: 20,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 14,
      flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flex: '1 1 320px' }}>
        <Sparkles size={18} style={{ color: PINK, flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-primary)' }}>
            {title}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.5 }}>
            {body}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <a
          href={calendlyUrl(medium, link)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            background: PINK, color: '#fff', textDecoration: 'none',
            padding: '10px 18px', borderRadius: 999, fontSize: 13.5, fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          Book a call
        </a>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)',
            fontSize: 16, lineHeight: 1, padding: 6, fontFamily: 'inherit',
          }}
        >
          ✕
        </button>
      </div>
    </div>
  )
}
